import React, { Suspense, lazy } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import AppLayout from "@/components/Layout";
import { Spinner } from "@material-tailwind/react";
import { DynamicImportErrorBoundary } from "@/components/common/DynamicImportErrorBoundary";

const FallBack: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Suspense
      fallback={
        <div className="h-screen w-screen flex justify-center items-center">
          <Spinner className="h-20 w-20 text-gray-900/50" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
};

const Home = lazy(() => import("@/pages/home/index"));

const PrivateRoute: React.FC<{
  children: React.ReactNode;
  authKey: string;
}> = ({ children }) => {
  return children;
};

const LazyComponentWithAuth = (
  importFunc: () => Promise<{ default: React.ComponentType<any> }>,
  authKey: string = "",
) => {
  const Component = lazy(importFunc);

  return (
    <DynamicImportErrorBoundary>
      <FallBack>
        <PrivateRoute authKey={authKey}>
          <Component />
        </PrivateRoute>
      </FallBack>
    </DynamicImportErrorBoundary>
  );
};

const Routes = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    children: [
      {
        path: "/",
        element: (
          <DynamicImportErrorBoundary>
            <FallBack>
              <Home />
            </FallBack>
          </DynamicImportErrorBoundary>
        ),
      },
      {
        path: "/home/:code",
        element: (
          <DynamicImportErrorBoundary>
            <FallBack>
              <Home />
            </FallBack>
          </DynamicImportErrorBoundary>
        ),
      },
      {
        path: "/topic/:id",
        element: LazyComponentWithAuth(() => import("@/pages/topic/index")),
      },
      {
        path: "/topik",
        element: LazyComponentWithAuth(() => import("@/pages/topik/index")),
      },
      {
        path: "/resource",
        element: LazyComponentWithAuth(() => import("@/pages/topik/resource")),
      },
      {
        path: "/library",
        element: LazyComponentWithAuth(() => import("@/pages/library/index")),
      },
      {
        path: "/course-center",
        element: LazyComponentWithAuth(() => import("@/pages/course-center/index")),
      },
      {
        path: "/category/:id",
        element: LazyComponentWithAuth(() => import("@/pages/category/index")),
      },
      {
        path: "/category",
        element: LazyComponentWithAuth(() => import("@/pages/category/list")),
      },

      {
        path: "/word-list",
        element: LazyComponentWithAuth(() => import("@/pages/word/list")),
      },
      {
        path: "/open",
        element: LazyComponentWithAuth(() => import("@/pages/word/open")),
      },
      {
        path: "/word",
        element: LazyComponentWithAuth(() => import("@/pages/word/index")),
      },

      {
        path: "/word/:type",
        element: LazyComponentWithAuth(() => import("@/pages/word/index")),
      },

      {
        path: "/grammer",
        element: LazyComponentWithAuth(() => import("@/pages/grammer/index")),
      },
      {
        path: "/word/unfamiliar",
        element: LazyComponentWithAuth(() => import("@/pages/word/unfamiliar")),
      },
      {
        path: "/word/calendar",
        element: LazyComponentWithAuth(() => import("@/pages/word/calendar")),
      },

      {
        path: "/word/notes",
        element: LazyComponentWithAuth(() => import("@/pages/word/notes")),
      },
      {
        path: "/word/card-mode",
        element: LazyComponentWithAuth(() => import("@/pages/word/card-mode")),
      },
      {
        path: "/about",
        element: LazyComponentWithAuth(() => import("@/pages/about/index")),
      },
      {
        path: "/dictation-sentence",
        element: LazyComponentWithAuth(
          () => import("@/pages/dictation-sentence/index"),
        ),
      },
    ],
  },
  {
    path: "/word/review-calendar",
    element: LazyComponentWithAuth(
      () => import("@/pages/word/review-calendar"),
    ),
  },
  {
    path: "/word/review-session",
    element: LazyComponentWithAuth(
      () => import("@/pages/word/review-session"),
    ),
  },
  {
    path: "/:type/:appId",
    element: LazyComponentWithAuth(() => import("@/pages/chat/index")),
  },
  {
    path: "/word/audio",
    element: LazyComponentWithAuth(() => import("@/pages/word/audio")),
  },
  {
    path: "/:type/:appId/:id",
    element: LazyComponentWithAuth(() => import("@/pages/chat/index")),
  },
  {
    path: "/dictation/error",
    element: LazyComponentWithAuth(() => import("@/pages/dictation/index")),
  },
  {
    path: "/dictation/practice",
    element: LazyComponentWithAuth(() => import("@/pages/dictation/practice")),
  },
  {
    path: "/dictation/practice-list",
    element: LazyComponentWithAuth(
      () => import("@/pages/dictation/practice-list"),
    ),
  },
  {
    path: "/question",
    element: LazyComponentWithAuth(() => import("@/pages/question/index")),
  },
  {
    path: "/question/:id",
    element: LazyComponentWithAuth(() => import("@/pages/question/detail")),
  },
  {
    path: "/403",
    element: LazyComponentWithAuth(() => import("@/pages/403")),
  },
  {
    path: "/vip",
    element: LazyComponentWithAuth(() => import("@/pages/vip/index")),
  },
  {
    path: "/subscribe",
    element: LazyComponentWithAuth(() => import("@/pages/subscribe/index")),
  },
  {
    path: "/article/category",
    element: LazyComponentWithAuth(() => import("@/pages/article/category")),
  },
  {
    path: "/article/list/:id",
    element: LazyComponentWithAuth(() => import("@/pages/article/list")),
  },
  {
    path: "/article/detail/:id",
    element: LazyComponentWithAuth(() => import("@/pages/article/detail")),
  },
  {
    path: "*",
    element: LazyComponentWithAuth(() => import("@/pages/404")),
  },
]);

export const AppRouter = () => {
  return (
    <DynamicImportErrorBoundary>
      <RouterProvider router={Routes} />
    </DynamicImportErrorBoundary>
  );
};
